/**
 * Similar Job Card Component
 * 
 * Extends the existing JobCard with similarity information and match details
 * Now includes expandable drawer with Video, Details, and Results buttons
 */

import React, { useState, useEffect } from 'react';
import { 
  FaCar, 
  FaIdCard,
  FaVideo,
  FaFileAlt,
  FaSearchPlus
} from 'react-icons/fa';
import JobCard from './JobCard';
import navigationService from '../../../../services/NavigationService';
import { useNotifications } from '../../../notifications/NotificationsProvider';
import SharedVideoPlayer from '../reportCards/shared/SharedVideoPlayer';
import '../../styles/cards.css';

const SimilarJobCard = ({ 
  similarJob, 
  onJobSelect, 
  size = 'regular',
  collapsible = true, // Now collapsible by default
  defaultCollapsed = true, // Start collapsed
  isCollapsed = null,
  onCollapseChange = null,
  hideDetailsButton = true, // Hide Details button on card itself (use drawer instead)
  currentUser = null,
  onVideoRequest = null, // Callback when video button clicked (handles privacy)
  onResultsRequest = null // Callback when results button clicked (handles privacy)
}) => {
  const { notify } = useNotifications();
  
  // Use internal state if no external control
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const collapsed = isCollapsed !== null ? isCollapsed : internalCollapsed;
  
  // Video player state
  const [showVideo, setShowVideo] = useState(false);
  
  // Hide video when card is collapsed
  useEffect(() => {
    if (collapsed) {
      setShowVideo(false);
    }
  }, [collapsed]);
  
  // Create a job object that matches the JobCard expected format
  const jobForCard = {
    id: similarJob.jobId,
    provider: similarJob.provider,
    status: 'completed', // Similar jobs are always completed
    evidenceId: similarJob.evidenceId,
    casefileId: similarJob.casefileId,
    createdAt: similarJob.createdAt,
    orderId: similarJob.orderId,
    // Use actual job data instead of fake similarity-match
    selectedServices: similarJob.selectedServices || [],
    selectedTargets: similarJob.selectedTargets || [],
    selectedModel: similarJob.selectedModel,
    serviceName: similarJob.serviceName,
    analysisRequest: similarJob.analysisRequest
  };

  // Create proper resultsMeta for the similar job
  const resultsMeta = {
    targets: similarJob.analysisRequest?.targets || similarJob.selectedTargets || similarJob.selectedServices || [],
    modelName: similarJob.selectedModel
  };

  // Get exact match type icons only (exact_vehicle and exact_plate)
  const getMatchTypeIcons = (matchTypes) => {
    const icons = [];
    
    // Only show exact matches
    if (matchTypes.includes('exact_plate')) {
      icons.push(<FaIdCard key="exact_plate" className="match-icon plate" />);
    }
    if (matchTypes.includes('exact_vehicle')) {
      icons.push(<FaCar key="exact_vehicle" className="match-icon vehicle" />);
    }
    
    return icons;
  };



  // Handle card collapse toggle
  const handleToggleCollapse = () => {
    const newCollapsed = !collapsed;
    if (isCollapsed === null) {
      setInternalCollapsed(newCollapsed);
    }
    if (onCollapseChange) {
      onCollapseChange(newCollapsed);
    }
  };

  // Check privacy for evidence (video)
  const isOwner = currentUser && similarJob && (
    currentUser.uid === similarJob.userId || 
    currentUser.uid === similarJob.createdBy
  );
  const evidenceIsPublic = similarJob?.evidenceIsPublic || false;
  const jobIsPublic = similarJob?.isPublic || false;
  const hasEvidenceAccess = isOwner || evidenceIsPublic;
  const hasResultsAccess = isOwner || jobIsPublic;

  // Handle Video button click
  const handleVideoClick = (e) => {
    e.stopPropagation();
    
    if (hasEvidenceAccess) {
      // User has access - toggle video player
      setShowVideo(!showVideo);
      if (onVideoRequest) {
        onVideoRequest(similarJob);
      }
    } else {
      // Show purchase prompt for evidence access
      notify({ 
        severity: 'info', 
        title: 'Premium Content', 
        message: 'Purchase or request access to view this evidence.' 
      });
      // Could trigger purchase modal here
      if (onVideoRequest) {
        onVideoRequest(similarJob, 'purchase');
      }
    }
  };

  // Handle Details button click - Navigate to SingleJobResults
  const handleDetailsClick = (e) => {
    e.stopPropagation();
    
    const orderId = similarJob.orderId || similarJob.order_id;
    const jobId = similarJob.jobId || similarJob.id;
    
    console.log('📄 Navigating to job details:', orderId, jobId);
    navigationService.navigateToOrder(orderId, jobId, { source: 'similar_job_details_button' });
  };

  // Handle Results button click (red magnifying glass)
  const handleResultsClick = (e) => {
    e.stopPropagation();
    
    if (hasResultsAccess) {
      // User has access - show results
      if (onResultsRequest) {
        onResultsRequest(similarJob);
      } else if (onJobSelect) {
        // Fallback to onJobSelect if no specific handler
        const jobData = {
          ...similarJob,
          orderId: similarJob.orderId || similarJob.order_id,
          jobId: similarJob.jobId || similarJob.id
        };
        onJobSelect(jobData);
      }
    } else {
      // Show purchase prompt for results access
      notify({ 
        severity: 'info', 
        title: 'Premium Content', 
        message: 'Purchase or request access to view these results.' 
      });
      // Could trigger purchase modal here
      if (onResultsRequest) {
        onResultsRequest(similarJob, 'purchase');
      }
    }
  };

  return (
    <div className="similar-job-card-wrapper">
      {/* Enhanced Job Card with minimal similarity overlay */}
      <div className="job-card-container">
        <JobCard 
          job={jobForCard} 
          resultsMeta={resultsMeta} 
          size={size}
          onOpen={handleToggleCollapse}
          collapsible={collapsible}
          defaultCollapsed={defaultCollapsed}
          isCollapsed={collapsed}
          onCollapseChange={(newCollapsed) => {
            // Update internal state
            if (isCollapsed === null) {
              setInternalCollapsed(newCollapsed);
            }
            // Call parent callback if provided
            if (onCollapseChange) {
              onCollapseChange(newCollapsed);
            }
          }}
          hideDetailsButton={hideDetailsButton}
        />
        
        {/* Minimal Similarity Indicator */}
        <div className="similarity-overlay">
          <div className="similarity-indicator">
            {getMatchTypeIcons(similarJob.matchTypes)}
          </div>
        </div>
      </div>

      {/* Three-Button Drawer - Shows when expanded */}
      {!collapsed && (
        <>
          <div className="similar-job-drawer">
            <div className="drawer-buttons">
              {/* Video Button */}
              <button
                className={`drawer-btn ${hasEvidenceAccess ? (showVideo ? 'btn-active' : 'btn-secondary') : 'btn-locked'}`}
                onClick={handleVideoClick}
                title={hasEvidenceAccess ? 'View evidence video' : 'Purchase access to view video'}
              >
                <FaVideo />
                <span>{showVideo ? 'Hide Video' : 'Video'}</span>
                {!hasEvidenceAccess && <span className="lock-badge">🔒</span>}
              </button>

              {/* Details Button - Navigate to full job view */}
              <button
                className="drawer-btn btn-secondary"
                onClick={handleDetailsClick}
                title="View full job details page"
              >
                <FaFileAlt />
                <span>Details</span>
              </button>

              {/* Results Button - Red magnifying glass */}
              <button
                className={`drawer-btn ${hasResultsAccess ? 'btn-results' : 'btn-locked'}`}
                onClick={handleResultsClick}
                title={hasResultsAccess ? 'View analysis results' : 'Purchase access to view results'}
              >
                <FaSearchPlus />
                <span>Results</span>
                {!hasResultsAccess && <span className="lock-badge">🔒</span>}
              </button>
            </div>
          </div>

          {/* Expanded Video Player */}
          {showVideo && hasEvidenceAccess && (
            <div className="similar-job-video-player">
              {similarJob.evidenceUrl || similarJob.evidenceFiles?.[0]?.downloadURL ? (
                <SharedVideoPlayer
                  src={similarJob.evidenceUrl || similarJob.evidenceFiles?.[0]?.downloadURL}
                  poster={similarJob.evidenceThumbnail || similarJob.evidenceFiles?.[0]?.thumbnailURL}
                  fitMode="contain"
                />
              ) : (
                <div className="video-unavailable">
                  <p>Video not available for this evidence</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SimilarJobCard;
